import { useCallback, useEffect, useState } from "react";
import { motion } from "framer-motion";
import { addMonths, format } from "date-fns";
import { ChevronLeft, ChevronRight, Pencil, Plus, Trash2, TrendingDown, TrendingUp, Wallet } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";

const CATEGORIES = {
    revenue: ["Rental", "Deposit", "Other income"],
    expense: ["Fuel", "Insurance", "Equipment", "Repairs", "Marketing", "Storage", "Other expense"],
};

const fieldCls = "rounded-xl border-2 border-monster-navy bg-white px-3 py-2 text-sm font-semibold shadow-sticker-sm";
const money = (n) => (n ?? 0).toLocaleString("en-US", { style: "currency", currency: "USD" });

export const AdminLedger = () => {
    const [month, setMonth] = useState(() => {
        const d = new Date();
        return new Date(d.getFullYear(), d.getMonth(), 1);
    });
    const [data, setData] = useState(null);
    const [type, setType] = useState("revenue");
    const [amount, setAmount] = useState("");
    const [category, setCategory] = useState(CATEGORIES.revenue[0]);
    const [description, setDescription] = useState("");
    const [date, setDate] = useState(format(new Date(), "yyyy-MM-dd"));
    const [saving, setSaving] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const [method, setMethod] = useState("cash");

    const load = useCallback(() => {
        api.get(`/ledger?month=${format(month, "yyyy-MM")}`)
            .then((res) => setData(res.data))
            .catch((err) => toast.error(formatApiError(err)));
    }, [month]);

    useEffect(load, [load]);

    const switchType = (t) => {
        setType(t);
        setCategory(CATEGORIES[t][0]);
    };

    const resetForm = () => {
        setEditingId(null);
        setAmount("");
        setDescription("");
        setDate(format(new Date(), "yyyy-MM-dd"));
        setMethod("cash");
    };

    const startEdit = (entry) => {
        setEditingId(entry.id);
        setType(entry.type);
        setCategory(entry.category);
        setAmount(String(entry.amount));
        setDescription(entry.description || "");
        setDate(entry.date);
        setMethod(entry.method || "cash");
        document.querySelector('[data-testid="ledger-form"]')?.scrollIntoView({ behavior: "smooth", block: "center" });
    };

    const saveEntry = async (e) => {
        e.preventDefault();
        setSaving(true);
        try {
            const payload = { type, amount: parseFloat(amount), category, description, date, method };
            if (editingId) {
                await api.patch(`/ledger/${editingId}`, payload);
                toast.success("Entry updated");
            } else {
                await api.post("/ledger", payload);
                toast.success(`${type === "revenue" ? "Income" : "Expense"} added`);
            }
            resetForm();
            load();
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setSaving(false);
        }
    };

    const remove = async (id) => {
        try {
            await api.delete(`/ledger/${id}`);
            toast.success("Entry deleted");
            load();
        } catch (err) {
            toast.error(formatApiError(err));
        }
    };

    const stats = [
        { label: "Income", value: data?.revenue ?? 0, icon: TrendingUp, bg: "bg-monster-green", testId: "ledger-income-card" },
        { label: "Expenses", value: data?.expenses ?? 0, icon: TrendingDown, bg: "bg-monster-red", testId: "ledger-expense-card" },
        { label: "Net", value: data?.net ?? 0, icon: Wallet, bg: "bg-monster-navy", testId: "ledger-net-card" },
    ];

    return (
        <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ type: "spring", stiffness: 100, damping: 15 }}
            className="space-y-8"
            data-testid="admin-ledger"
        >
            <div className="flex flex-wrap items-center justify-between gap-4">
                <h2 className="font-display text-2xl font-semibold text-monster-navy">Money Ledger</h2>
                <div className="flex items-center gap-2">
                    <button data-testid="ledger-prev-month" onClick={() => setMonth((m) => addMonths(m, -1))} className="rounded-full border-2 border-monster-navy bg-white p-2 shadow-sticker-sm transition-transform hover:-translate-y-0.5" aria-label="Previous month">
                        <ChevronLeft className="h-4 w-4" />
                    </button>
                    <span className="min-w-[140px] rounded-full border-2 border-monster-navy bg-white px-4 py-2 text-center font-display text-sm font-semibold text-monster-navy shadow-sticker-sm" data-testid="ledger-month-label">
                        {format(month, "MMMM yyyy")}
                    </span>
                    <button data-testid="ledger-next-month" onClick={() => setMonth((m) => addMonths(m, 1))} className="rounded-full border-2 border-monster-navy bg-white p-2 shadow-sticker-sm transition-transform hover:-translate-y-0.5" aria-label="Next month">
                        <ChevronRight className="h-4 w-4" />
                    </button>
                </div>
            </div>

            <div className="grid grid-cols-3 gap-4">
                {stats.map((s) => (
                    <div key={s.label} data-testid={s.testId} className={`rounded-3xl border-2 border-monster-navy p-4 shadow-sticker-sm sm:p-5 ${s.bg}`}>
                        <s.icon className={`h-5 w-5 ${s.bg === "bg-monster-navy" ? "text-monster-yellow" : "text-monster-navy"}`} />
                        <div className={`mt-2 font-display text-lg font-semibold sm:text-2xl ${s.bg === "bg-monster-navy" ? (s.value < 0 ? "text-monster-red" : "text-monster-green") : "text-monster-navy"}`}>
                            {s.label === "Net" && s.value < 0 ? `-${money(Math.abs(s.value))}` : money(s.value)}
                        </div>
                        <div className={`text-[10px] font-bold uppercase tracking-wider sm:text-xs ${s.bg === "bg-monster-navy" ? "text-white/60" : "text-monster-navy/60"}`}>{s.label}</div>
                    </div>
                ))}
            </div>

            <div data-testid="ledger-ytd-strip" className="flex flex-wrap items-center justify-between gap-4 rounded-3xl border-2 border-monster-navy bg-monster-navy px-6 py-5 shadow-sticker">
                <span className="font-display text-sm font-semibold uppercase tracking-[0.2em] text-monster-yellow">{data?.year ?? format(month, "yyyy")} — Year to Date</span>
                <div className="flex flex-wrap gap-6">
                    <div>
                        <div className="font-display text-xl font-semibold text-monster-green" data-testid="ledger-ytd-income">{money(data?.ytd_income ?? 0)}</div>
                        <div className="text-[10px] font-bold uppercase tracking-wider text-white/50">Income YTD</div>
                    </div>
                    <div>
                        <div className="font-display text-xl font-semibold text-monster-red" data-testid="ledger-ytd-expenses">{money(data?.ytd_expenses ?? 0)}</div>
                        <div className="text-[10px] font-bold uppercase tracking-wider text-white/50">Payouts YTD</div>
                    </div>
                    <div>
                        <div className={`font-display text-xl font-semibold ${(data?.ytd_net ?? 0) < 0 ? "text-monster-red" : "text-white"}`} data-testid="ledger-ytd-net">
                            {(data?.ytd_net ?? 0) < 0 ? `-${money(Math.abs(data.ytd_net))}` : money(data?.ytd_net ?? 0)}
                        </div>
                        <div className="text-[10px] font-bold uppercase tracking-wider text-white/50">Net YTD</div>
                    </div>
                </div>
            </div>

            <form data-testid="ledger-form" onSubmit={saveEntry} className="rounded-3xl border-2 border-monster-navy bg-white p-6 shadow-sticker sm:p-8">
                <h3 className="font-display text-lg font-semibold text-monster-navy">{editingId ? "Edit entry" : "Add an entry"}</h3>
                <div className="mt-4 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
                    <div className="flex gap-2">
                        <button
                            type="button"
                            data-testid="ledger-type-revenue"
                            onClick={() => switchType("revenue")}
                            className={`flex-1 rounded-full border-2 border-monster-navy px-4 py-2.5 font-display text-sm font-semibold shadow-sticker-sm transition-all ${type === "revenue" ? "bg-monster-green text-monster-navy" : "bg-white text-monster-navy/50"}`}
                        >
                            Income
                        </button>
                        <button
                            type="button"
                            data-testid="ledger-type-expense"
                            onClick={() => switchType("expense")}
                            className={`flex-1 rounded-full border-2 border-monster-navy px-4 py-2.5 font-display text-sm font-semibold shadow-sticker-sm transition-all ${type === "expense" ? "bg-monster-red text-white" : "bg-white text-monster-navy/50"}`}
                        >
                            Expense
                        </button>
                    </div>
                    <div>
                        <Label htmlFor="ledger-amount" className="sr-only">Amount</Label>
                        <Input id="ledger-amount" data-testid="ledger-amount-input" required type="number" min="0.01" step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="Amount ($)" className={fieldCls} />
                    </div>
                    <div>
                        <Select value={category} onValueChange={setCategory}>
                            <SelectTrigger data-testid="ledger-category-select" className={fieldCls}>
                                <SelectValue placeholder="Category" />
                            </SelectTrigger>
                            <SelectContent>
                                {CATEGORIES[type].map((c) => (
                                    <SelectItem key={c} value={c} data-testid={`ledger-category-${c.toLowerCase().replace(/\s/g, "-")}`}>{c}</SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                    <div>
                        <Label htmlFor="ledger-date" className="sr-only">Date</Label>
                        <Input id="ledger-date" data-testid="ledger-date-input" required type="date" value={date} onChange={(e) => setDate(e.target.value)} className={fieldCls} />
                    </div>
                    {type === "revenue" && (
                        <div>
                            <Select value={method} onValueChange={setMethod}>
                                <SelectTrigger data-testid="ledger-method-select" className={fieldCls}>
                                    <SelectValue placeholder="How was it paid?" />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="cash" data-testid="ledger-method-cash">Cash</SelectItem>
                                    <SelectItem value="card" data-testid="ledger-method-card">Card</SelectItem>
                                    <SelectItem value="venmo" data-testid="ledger-method-venmo">Venmo</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                    )}
                    <div className="sm:col-span-2 lg:col-span-3">
                        <Label htmlFor="ledger-description" className="sr-only">Description</Label>
                        <Input id="ledger-description" data-testid="ledger-description-input" value={description} onChange={(e) => setDescription(e.target.value)} placeholder="What's it for? (e.g. Blower repair, Smith party rental)" className={fieldCls} />
                    </div>
                    <div className="flex gap-2">
                        <button
                            data-testid="ledger-add-btn"
                            type="submit"
                            disabled={saving}
                            className="inline-flex flex-1 items-center justify-center gap-2 rounded-full border-2 border-monster-navy bg-monster-blue px-6 py-2.5 font-display text-sm font-semibold text-white shadow-sticker-sm transition-all hover:-translate-y-0.5 active:translate-y-1 active:shadow-none disabled:opacity-60"
                        >
                            <Plus className="h-4 w-4" /> {saving ? "Saving..." : editingId ? "Save Changes" : "Add Entry"}
                        </button>
                        {editingId && (
                            <button
                                type="button"
                                data-testid="ledger-cancel-edit-btn"
                                onClick={resetForm}
                                className="rounded-full border-2 border-monster-navy bg-white px-5 py-2.5 font-display text-sm font-semibold text-monster-navy shadow-sticker-sm transition-all hover:-translate-y-0.5"
                            >
                                Cancel
                            </button>
                        )}
                    </div>
                </div>
            </form>

            <div className="overflow-hidden rounded-3xl border-2 border-monster-navy bg-white shadow-sticker">
                <div className="border-b-2 border-monster-navy px-6 py-4">
                    <h3 className="font-display text-lg font-semibold text-monster-navy">{format(month, "MMMM yyyy")} entries</h3>
                </div>
                {!data ? (
                    <p className="p-10 text-center font-semibold text-monster-navy/50">Loading...</p>
                ) : data.entries.length === 0 ? (
                    <p data-testid="ledger-empty" className="p-10 text-center font-semibold text-monster-navy/50">
                        No entries this month yet — add your first one above.
                    </p>
                ) : (
                    <div className="overflow-x-auto">
                        <Table>
                            <TableHeader>
                                <TableRow className="border-b-2 border-monster-navy">
                                    <TableHead className="font-display font-semibold">Date</TableHead>
                                    <TableHead className="font-display font-semibold">Description</TableHead>
                                    <TableHead className="font-display font-semibold">Category</TableHead>
                                    <TableHead className="font-display font-semibold text-right">Amount</TableHead>
                                    <TableHead className="font-display font-semibold">Actions</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {data.entries.map((e) => (
                                    <TableRow key={e.id} data-testid={`ledger-row-${e.id}`} className="border-b border-monster-navy/10">
                                        <TableCell className="whitespace-nowrap font-bold">{e.date}</TableCell>
                                        <TableCell className="max-w-[240px] truncate font-semibold">{e.description || "—"}</TableCell>
                                        <TableCell>
                                            <span className={`rounded-full border-2 border-monster-navy px-3 py-1 text-xs font-bold ${e.type === "revenue" ? "bg-monster-green/20 text-monster-navy" : "bg-monster-red/15 text-monster-navy"}`}>
                                                {e.category}
                                            </span>
                                        </TableCell>
                                        <TableCell className={`text-right font-display text-base font-semibold ${e.type === "revenue" ? "text-monster-green" : "text-monster-red"}`}>
                                            {e.type === "revenue" ? "+" : "-"}{money(e.amount)}
                                        </TableCell>
                                        <TableCell>
                                            <div className="flex items-center gap-1.5">
                                                <button data-testid={`ledger-edit-${e.id}`} onClick={() => startEdit(e)} className="rounded-lg border-2 border-monster-navy bg-white p-1.5 text-monster-blue transition-colors hover:bg-monster-sky" aria-label="Edit entry">
                                                    <Pencil className="h-4 w-4" />
                                                </button>
                                                <button data-testid={`ledger-delete-${e.id}`} onClick={() => remove(e.id)} className="rounded-lg border-2 border-monster-navy bg-white p-1.5 text-monster-red transition-colors hover:bg-red-50" aria-label="Delete entry">
                                                    <Trash2 className="h-4 w-4" />
                                                </button>
                                            </div>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </div>
                )}
            </div>
        </motion.div>
    );
};
